// ─────────────────────────────────────────────────────────────────
// Block style — border
// ----------------------------------------------------------------
// `style.border` (a shorthand) and `style.borderColor` are in
// BlockStyleSchema, and both renderers map them straight through: web as
// `border` / `border-color`, email through `emailInlineStyle`, which only drops
// `flexDirection` and `gap`. What was missing was a way to *set* them — the
// inspector had a single free-text field labelled Border with a numeric
// placeholder, so typing the `2` it asked for stored `border: 2`, which no
// client accepts, and `borderColor` had no control at all.
//
// So this control asks for the three parts a border is actually made of and
// composes a value that cannot be invalid. What it reads has to be just as
// forgiving, because years of documents were written by that text field.
// ─────────────────────────────────────────────────────────────────

/** How many sides a border can have. `none` clears it. */
export const BORDER_STYLES = ['solid', 'dashed', 'dotted'] as const;
export type BorderStyle = (typeof BORDER_STYLES)[number];

export interface BorderParts {
  /** Width in px as text ('' when there is no border). */
  width: string;
  style: BorderStyle;
  /** Colour as text ('' when unset) — from `borderColor`, or a shorthand that carried one. */
  color: string;
}

/** `2` or `2px` — what the old text field stored for "a 2px border, please". */
const BARE_WIDTH = /^(\d+(?:\.\d+)?)(px)?$/;

/** `1px solid`, `1px dashed #ccc`, `2px dotted rgb(0 0 0)` — a shorthand with a width. */
const SHORTHAND = /^(\d+(?:\.\d+)?)(?:px)?\s+(solid|dashed|dotted)(?:\s+(.+))?$/;

/** The width, style and colour a stored border amounts to. */
export function borderParts(
  border: string | undefined,
  borderColor: string | undefined,
): BorderParts {
  const stored = border?.trim() ?? '';
  const shorthand = SHORTHAND.exec(stored);
  if (shorthand) {
    return {
      width: shorthand[1] ?? '',
      style: (shorthand[2] as BorderStyle | undefined) ?? 'solid',
      color: borderColor?.trim() || shorthand[3]?.trim() || '',
    };
  }

  // A bare number is what the field used to invite, and reading it back as a
  // width is what the author meant by it — it just never reached a client,
  // because `border: 2` is not a border.
  const bare = BARE_WIDTH.exec(stored);
  if (bare && Number(bare[1]) > 0) {
    return { width: bare[1] ?? '', style: 'solid', color: borderColor?.trim() ?? '' };
  }

  return { width: '', style: 'solid', color: borderColor?.trim() ?? '' };
}

/**
 * The two style keys a set of parts means, both `undefined` when there is
 * nothing to write. A width of 0 draws nothing, so it clears the border rather
 * than storing `0px solid`.
 */
export function borderStyle(parts: BorderParts): {
  border: string | undefined;
  borderColor: string | undefined;
} {
  const width = Number(parts.width);
  const drawn = parts.width.trim() !== '' && Number.isFinite(width) && width > 0;
  return {
    border: drawn ? `${width}px ${parts.style}` : undefined,
    borderColor: parts.color.trim() === '' ? undefined : parts.color.trim(),
  };
}

interface BorderFieldProps {
  /** `style.border` as stored. */
  border: string | undefined;
  /** `style.borderColor` as stored. */
  borderColor: string | undefined;
  /** Writes both keys back; either may be `undefined` to clear it. */
  onChange: (next: { border: string | undefined; borderColor: string | undefined }) => void;
  label?: string;
}

/**
 * Width, style and colour for `style.border` + `style.borderColor`. The colour
 * defaults to the theme's text colour only for display — a border with no
 * colour of its own is drawn in the text colour, so that is what the swatch
 * shows.
 */
export function BorderField({ border, borderColor, onChange, label = 'Border' }: BorderFieldProps) {
  const parts = borderParts(border, borderColor);

  const write = (next: Partial<BorderParts>) => onChange(borderStyle({ ...parts, ...next }));

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label className="field-label text-xs" htmlFor="block-border-width">
          {label}
        </label>
        {parts.width !== '' && (
          <button
            type="button"
            className="text-muted hover:text-ink-soft text-xs"
            onClick={() => write({ width: '' })}
          >
            Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-1.5">
        <input
          id="block-border-width"
          title="Border width in px"
          type="number"
          min="0"
          step="1"
          placeholder="width"
          className="border-line bg-bg-soft h-9 w-full rounded-lg border p-1"
          value={parts.width}
          onChange={(e) => {
            const raw = e.target.value.trim();
            // `0` is how someone types "no border" — either spelling clears it.
            write({ width: raw === '0' ? '' : raw });
          }}
        />

        <select
          title="Border style"
          aria-label="Border style"
          className="field-input h-9 py-0 text-xs"
          value={parts.style}
          onChange={(e) => write({ style: e.target.value as BorderStyle })}
        >
          {BORDER_STYLES.map((style) => (
            <option key={style} value={style}>
              {style}
            </option>
          ))}
        </select>

        <input
          title="Border colour"
          aria-label="Border colour"
          type="color"
          className="border-line bg-bg-soft h-9 w-10 shrink-0 cursor-pointer rounded-lg border p-1"
          value={parts.color || '#1C2833'}
          onChange={(e) => write({ color: e.target.value })}
        />
      </div>

      {parts.color === '' && (
        <p className="text-muted mt-1 text-xs">
          No colour of its own — the border is drawn in the block's text colour. A width is what
          draws it at all.
        </p>
      )}
    </div>
  );
}
